import { fg } from '@atlaskit/platform-feature-flags/fg';

import { extensionBridgeStore } from './store';
import type { ExtensionControlBridge, WindowWithExtensionControl } from './types';

const getWindow = (): WindowWithExtensionControl | undefined =>
	typeof window === 'undefined' ? undefined : (window as WindowWithExtensionControl);

/**
 * Installs the CSP-safe extension / DevTools control bridge on
 * `window.postOffice.extensionControl` when `post-office_enable_extension-control`
 * is on.
 *
 * Returns a cleanup function that removes the bridge if it is still the one
 * installed by this call.
 */
export const installExtensionBridge = (): (() => void) => {
	const currentWindow = getWindow();
	if (!currentWindow) {
		return () => {};
	}

	if (!fg('post-office_enable_extension-control')) {
		if (currentWindow.postOffice?.extensionControl) {
			currentWindow.postOffice.extensionControl = undefined;
		}
		return () => {};
	}

	const bridge: ExtensionControlBridge = {
		clearPlacementConfig: (placementId) =>
			extensionBridgeStore.clearPlacementSimulationConfig(placementId),
		getConfig: () => extensionBridgeStore.getSimulationConfig(),
		getPlacementConfig: (placementId) =>
			extensionBridgeStore.getPlacementSimulationConfig(placementId),
		getScreenPlacements: () => extensionBridgeStore.getScreenPlacementsForExtension(),
		reset: () => extensionBridgeStore.resetSimulationConfig(),
		setPlacementConfig: (placementId, config) =>
			extensionBridgeStore.setPlacementSimulationConfig(placementId, config),
		subscribe: (listener) => extensionBridgeStore.subscribeSimulationConfig(listener),
	};

	currentWindow.postOffice = {
		...currentWindow.postOffice,
		extensionControl: bridge,
	};

	return () => {
		if (currentWindow.postOffice?.extensionControl === bridge) {
			currentWindow.postOffice.extensionControl = undefined;
		}
	};
};
